/**
 * Cover & Brand panel - the workbench surface for a SOW template's visual identity. It
 * lists EVERY detected image SEPARATELY, by location: the cover background, each header/
 * footer logo, and each body/inline image (labelled by its section). Each is confirmed-or-
 * overridden independently (CLAUDE.md "Confirm-or-override is the playbook default"): Keep /
 * Replace (upload) / Remove, and Swap-to-workspace for logos. When the document has no cover
 * or no logo, the admin is offered to Add one. Edits are applied on save by the workbench via
 * the shared `applyVisualOverrides` (the same helper the AI authoring flow uses), so the
 * per-asset rules live in one place.
 *
 * In view mode it renders nothing when there are no assets. In edit mode it also offers the
 * add-cover / add-logo rows when those are missing.
 */

import type { DocumentContract, LocatedAssetSlot, VisualAssetAction } from '@protoss/doc';
import { collectVisualAssetSlots, type VisualOverrides } from '@protoss/doc';
import { Button } from '@protoss/ui';
import * as React from 'react';
import { apiFetch } from '../../../../lib/apiClient';
import { useProtectedImageUrl } from '../../../../hooks/useProtectedImageUrl';
import { uploadErrorMessage, uploadPromptFile } from '../../../ai-chat/lib/promptUploadTargets';

/**
 * A detected SOW asset's location maps to a workspace Brand Asset use-case when it can be
 * promoted into the shared library (push direction). Body images are content, not brand.
 */
const PROMOTE_USE_CASE: Partial<Record<LocatedAssetSlot['location'], string>> = {
  cover: 'cover-background',
  header: 'document-logo',
  footer: 'footer-logo',
};

export interface SowWorkspaceBrand {
  headerLogoAssetUuid: string | null;
  documentLogoAssetUuid: string | null;
  coverBackgroundAssetUuid: string | null;
  footerLogoAssetUuid: string | null;
  primaryColor: string | null;
}

export interface SowCoverBrandPanelProps {
  contract: DocumentContract;
  /** slot → workspace-asset-uuid map from the template's metadata.assetSlots. */
  assetSlots: Record<string, string>;
  workspaceBrand: SowWorkspaceBrand;
  isEditing: boolean;
  overrides: VisualOverrides;
  onChange: (next: VisualOverrides) => void;
}

function assetContentUrl(uuid: string | null | undefined): string | null {
  return uuid ? `/api/workspace-assets/${uuid}/content` : null;
}

/** Human label for an asset by location (body images use their section heading). */
function assetLabel(a: LocatedAssetSlot): string {
  switch (a.location) {
    case 'cover':
      return 'Cover background';
    case 'header':
      return 'Header logo';
    case 'footer':
      return 'Footer logo';
    case 'body':
      return a.sectionHeading ? `Image - ${a.sectionHeading}` : 'Body image';
  }
}

const actionOf = (o: VisualOverrides, slot: string): VisualAssetAction =>
  o.assets?.find((a) => a.slot === slot)?.action ?? 'keep';
const providedUuidOf = (o: VisualOverrides, slot: string): string | undefined =>
  o.assets?.find((a) => a.slot === slot)?.assetUuid;

/** Set (or clear, for 'keep') the per-slot decision, immutably. */
function withAssetAction(
  o: VisualOverrides,
  slot: string,
  action: VisualAssetAction,
  assetUuid?: string
): VisualOverrides {
  const rest = (o.assets ?? []).filter((a) => a.slot !== slot);
  if (action === 'keep') return { ...o, assets: rest.length ? rest : undefined };
  return { ...o, assets: [...rest, { slot, action, ...(assetUuid ? { assetUuid } : {}) }] };
}

function AssetThumb({ url, alt, dimmed }: { url: string | null; alt: string; dimmed?: boolean }) {
  if (!url) return <div className="ptp-sow-brand__thumb ptp-sow-brand__thumb--empty" aria-hidden />;
  return (
    <div className={`ptp-sow-brand__thumb${dimmed ? ' ptp-sow-brand__thumb--removed' : ''}`}>
      <img className="ptp-sow-brand__thumb-img" src={url} alt={alt} />
    </div>
  );
}

function ActionButton({
  label,
  active,
  onClick,
  disabled,
}: {
  label: string;
  active: boolean;
  onClick: () => void;
  disabled?: boolean;
}) {
  return (
    <Button
      type="button"
      size="sm"
      variant={active ? 'primary' : 'outline'}
      aria-pressed={active}
      disabled={disabled}
      onClick={onClick}
    >
      {label}
    </Button>
  );
}

/** One row for an EXISTING detected asset: keep / replace / remove (+ workspace for logos). */
function VisualAssetRow({
  asset,
  currentUuid,
  workspaceLogoUuid,
  overrides,
  onChange,
  isEditing,
  onError,
}: {
  asset: LocatedAssetSlot;
  currentUuid: string | null;
  workspaceLogoUuid: string | null;
  overrides: VisualOverrides;
  onChange: (next: VisualOverrides) => void;
  isEditing: boolean;
  onError: (msg: string | null) => void;
}) {
  const [uploading, setUploading] = React.useState(false);
  const [promoting, setPromoting] = React.useState(false);
  const [promoted, setPromoted] = React.useState(false);
  const inputRef = React.useRef<HTMLInputElement>(null);
  const isLogo = asset.location === 'header' || asset.location === 'footer';
  // The detected asset can be saved into the workspace Brand Assets library as a reusable
  // default (push direction), so a logo/cover from this SOW becomes a workspace brand asset.
  const promoteUseCase = PROMOTE_USE_CASE[asset.location];

  const action = actionOf(overrides, asset.slot);
  const providedUuid = providedUuidOf(overrides, asset.slot);
  const displayUuid =
    action === 'replace' ? providedUuid : action === 'workspace' ? workspaceLogoUuid : currentUuid;
  const url = useProtectedImageUrl(assetContentUrl(displayUuid));

  const set = (next: VisualAssetAction) => onChange(withAssetAction(overrides, asset.slot, next));
  const promote = async () => {
    if (!currentUuid || !promoteUseCase) return;
    onError(null);
    setPromoting(true);
    try {
      await apiFetch<{ ok: boolean }>('/api/workspace/admin/brand/assets/promote', {
        method: 'POST',
        body: JSON.stringify({ assetUuid: currentUuid, useCase: promoteUseCase }),
      });
      setPromoted(true);
    } catch {
      onError('Could not save to Brand Assets. Please try again.');
    } finally {
      setPromoting(false);
    }
  };
  const upload = async (file: File | undefined) => {
    if (!file) return;
    onError(null);
    setUploading(true);
    try {
      const a = await uploadPromptFile('brand_asset', file);
      onChange(withAssetAction(overrides, asset.slot, 'replace', a.uuid));
    } catch (e) {
      onError(uploadErrorMessage(e));
    } finally {
      setUploading(false);
    }
  };

  return (
    <div className="ptp-sow-brand__row">
      <AssetThumb url={url} alt={assetLabel(asset)} dimmed={action === 'remove'} />
      <div className="ptp-sow-brand__controls">
        <span className="ptp-sow-brand__label">{assetLabel(asset)}</span>
        {isEditing ? (
          <>
            <div className="ptp-sow-brand__segmented" role="group">
              <ActionButton label="Keep" active={action === 'keep'} onClick={() => set('keep')} />
              {isLogo && workspaceLogoUuid ? (
                <ActionButton
                  label="Workspace logo"
                  active={action === 'workspace'}
                  onClick={() => set('workspace')}
                />
              ) : null}
              <ActionButton
                label="Replace"
                active={action === 'replace'}
                disabled={uploading}
                onClick={() => inputRef.current?.click()}
              />
              <ActionButton label="Remove" active={action === 'remove'} onClick={() => set('remove')} />
            </div>
            <input
              ref={inputRef}
              type="file"
              accept="image/png,image/jpeg"
              className="ptp-sow-brand__file-input"
              onChange={(e) => void upload(e.target.files?.[0])}
            />
            {uploading ? (
              <span className="ptp-sow-brand__hint">Uploading…</span>
            ) : action === 'replace' ? (
              <span className="ptp-sow-brand__hint">New image applied on save.</span>
            ) : action === 'workspace' ? (
              <span className="ptp-sow-brand__hint">Workspace brand logo applied on save.</span>
            ) : action === 'remove' ? (
              <span className="ptp-sow-brand__hint">Removed on save.</span>
            ) : null}
            {promoteUseCase && currentUuid && action !== 'remove' ? (
              promoted ? (
                <span className="ptp-sow-brand__hint">Saved to Brand Assets.</span>
              ) : (
                <Button
                  type="button"
                  size="sm"
                  variant="ghost"
                  disabled={promoting}
                  onClick={() => void promote()}
                >
                  {promoting ? 'Saving…' : 'Save to Brand Assets'}
                </Button>
              )
            ) : null}
          </>
        ) : (
          <span className="ptp-sow-brand__chip">{assetLabel(asset)}.</span>
        )}
      </div>
    </div>
  );
}

/** Add-when-missing row for a cover (the document had none). */
function AddCoverRow({
  overrides,
  onChange,
  onError,
}: {
  overrides: VisualOverrides;
  onChange: (next: VisualOverrides) => void;
  onError: (msg: string | null) => void;
}) {
  const [uploading, setUploading] = React.useState(false);
  const inputRef = React.useRef<HTMLInputElement>(null);
  const url = useProtectedImageUrl(assetContentUrl(overrides.addCoverAssetUuid));
  const added = Boolean(overrides.addCoverAssetUuid);
  const upload = async (file: File | undefined) => {
    if (!file) return;
    onError(null);
    setUploading(true);
    try {
      const a = await uploadPromptFile('brand_asset', file);
      onChange({ ...overrides, addCoverAssetUuid: a.uuid });
    } catch (e) {
      onError(uploadErrorMessage(e));
    } finally {
      setUploading(false);
    }
  };
  return (
    <div className="ptp-sow-brand__row">
      <AssetThumb url={url} alt="Cover background" />
      <div className="ptp-sow-brand__controls">
        <span className="ptp-sow-brand__label">Cover background</span>
        <div className="ptp-sow-brand__segmented" role="group">
          <ActionButton
            label="Add a cover"
            active={added}
            disabled={uploading}
            onClick={() => inputRef.current?.click()}
          />
          <ActionButton
            label="No cover"
            active={!added}
            onClick={() => onChange({ ...overrides, addCoverAssetUuid: undefined })}
          />
        </div>
        <input
          ref={inputRef}
          type="file"
          accept="image/png,image/jpeg"
          className="ptp-sow-brand__file-input"
          onChange={(e) => void upload(e.target.files?.[0])}
        />
        {uploading ? (
          <span className="ptp-sow-brand__hint">Uploading…</span>
        ) : added ? (
          <span className="ptp-sow-brand__hint">New cover applied on save.</span>
        ) : null}
      </div>
    </div>
  );
}

/** Add-when-missing row for a logo (the document had none). */
function AddLogoRow({
  workspaceLogoUuid,
  overrides,
  onChange,
  onError,
}: {
  workspaceLogoUuid: string | null;
  overrides: VisualOverrides;
  onChange: (next: VisualOverrides) => void;
  onError: (msg: string | null) => void;
}) {
  const [uploading, setUploading] = React.useState(false);
  const inputRef = React.useRef<HTMLInputElement>(null);
  const workspace = Boolean(overrides.addLogoWorkspace);
  const added = Boolean(overrides.addLogoAssetUuid);
  const url = useProtectedImageUrl(
    assetContentUrl(workspace ? workspaceLogoUuid : overrides.addLogoAssetUuid)
  );
  const upload = async (file: File | undefined) => {
    if (!file) return;
    onError(null);
    setUploading(true);
    try {
      const a = await uploadPromptFile('brand_asset', file);
      onChange({ ...overrides, addLogoAssetUuid: a.uuid, addLogoWorkspace: false });
    } catch (e) {
      onError(uploadErrorMessage(e));
    } finally {
      setUploading(false);
    }
  };
  return (
    <div className="ptp-sow-brand__row">
      <AssetThumb url={url} alt="Logo" />
      <div className="ptp-sow-brand__controls">
        <span className="ptp-sow-brand__label">Header / footer logo</span>
        <div className="ptp-sow-brand__segmented" role="group">
          {workspaceLogoUuid ? (
            <ActionButton
              label="Workspace logo"
              active={workspace}
              onClick={() =>
                onChange({ ...overrides, addLogoWorkspace: true, addLogoAssetUuid: undefined })
              }
            />
          ) : null}
          <ActionButton
            label="Add a logo"
            active={added}
            disabled={uploading}
            onClick={() => inputRef.current?.click()}
          />
          <ActionButton
            label="No logo"
            active={!added && !workspace}
            onClick={() =>
              onChange({ ...overrides, addLogoAssetUuid: undefined, addLogoWorkspace: false })
            }
          />
        </div>
        <input
          ref={inputRef}
          type="file"
          accept="image/png,image/jpeg"
          className="ptp-sow-brand__file-input"
          onChange={(e) => void upload(e.target.files?.[0])}
        />
        {uploading ? (
          <span className="ptp-sow-brand__hint">Uploading…</span>
        ) : added ? (
          <span className="ptp-sow-brand__hint">New logo applied on save.</span>
        ) : workspace ? (
          <span className="ptp-sow-brand__hint">Workspace brand logo applied on save.</span>
        ) : null}
      </div>
    </div>
  );
}

export function SowCoverBrandPanel({
  contract,
  assetSlots,
  workspaceBrand,
  isEditing,
  overrides,
  onChange,
}: SowCoverBrandPanelProps) {
  const { located } = React.useMemo(() => collectVisualAssetSlots(contract), [contract]);
  const workspaceLogoUuid =
    workspaceBrand.documentLogoAssetUuid ?? workspaceBrand.headerLogoAssetUuid ?? null;
  const [error, setError] = React.useState<string | null>(null);

  const hasCover = located.some((a) => a.location === 'cover');
  const hasLogo = located.some((a) => a.location === 'header' || a.location === 'footer');

  // View mode with nothing visual to show: render nothing. Edit mode also shows the
  // add-cover / add-logo affordances when those are missing.
  if (!isEditing && located.length === 0) return null;

  return (
    <section className="ptp-sow-brand" aria-label="Cover and brand">
      <div className="ptp-sow-brand__header">
        <span className="ptp-sow-brand__title">Cover &amp; brand</span>
      </div>

      {/* Grouped by where each asset lives: Cover -> Header -> Footer -> Body. */}
      {(['cover', 'header', 'footer', 'body'] as const).map((loc) => {
        const rows = located.filter((a) => a.location === loc);
        if (rows.length === 0) return null;
        const label =
          loc === 'cover' ? 'Cover' : loc === 'header' ? 'Header' : loc === 'footer' ? 'Footer' : 'Body';
        return (
          <React.Fragment key={loc}>
            <div className="ptp-sow-brand__group">{label}</div>
            {rows.map((asset) => (
              <VisualAssetRow
                key={asset.slot}
                asset={asset}
                currentUuid={assetSlots[asset.slot] ?? null}
                workspaceLogoUuid={workspaceLogoUuid}
                overrides={overrides}
                onChange={onChange}
                isEditing={isEditing}
                onError={setError}
              />
            ))}
          </React.Fragment>
        );
      })}

      {isEditing && !hasCover ? (
        <AddCoverRow overrides={overrides} onChange={onChange} onError={setError} />
      ) : null}
      {isEditing && !hasLogo ? (
        <AddLogoRow
          workspaceLogoUuid={workspaceLogoUuid}
          overrides={overrides}
          onChange={onChange}
          onError={setError}
        />
      ) : null}

      {error ? <span className="ptp-sow-brand__error">{error}</span> : null}
    </section>
  );
}
